/**
 * Hurdles (v1 plan sections 6.3 and 7.4): the vault, the mount, the hop over a
 * knee-high box, and the hit, with the slide's grade against a pipe.
 *
 * A vault is a speed vault. Running at a box, the legs take off early enough
 * that the flight's apex carries the hips vaultHipClearance over the top as the
 * body reaches the near face; the hands go onto the top and hold the body at
 * that height while the tucked legs pass beside the box (the one depth the
 * side view does not draw: the box is passed through by the lower body only
 * while the hands hold it). Past the far edge the body falls and lands (a
 * vault). If the hands cannot carry it past the far edge within
 * vaultSupportMs, they press the body up until the feet are over the top and it
 * comes down on the box (a mount). A knee-high box gets a hop whose tucked feet
 * clear it, with no hands and nothing passed through.
 */

import { comHeight, shoulderHeight } from "../body";
import { capsuleHeight } from "../collision";
import type { EventBuffer } from "../events";
import { abs, moveToward, sign, sqrt } from "../fastmath";
import { emitGrade, gradeOffset } from "../grades";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, Grade, GradeKind, type InputFrame, S, SolidClass } from "../types";
import type { World } from "../world";
import { setState } from "./transitions";

/** Plant phases. */
export const PlantPhase = {
  None: 0,
  /** Rising toward the box (the hands reach for its edge). */
  Takeoff: 1,
  /** Over the box, the hands holding the body up. */
  Support: 2,
  /** Hop only: past the takeoff, falling freely. */
  Released: 3,
  /** Too slow to clear: the hands press the body up onto the top. */
  Mounting: 4,
  /**
   * A mantle over a ledge (`ledge.ts`): the arms pull the body up the face until the shoulders clear
   * the top, then the hands press it up and over the edge.
   */
  Mantling: 5,
} as const;

function overlapsX(world: World, s: SimState, t: CompiledTuning, i: number): boolean {
  const r = t.capsuleRadius;
  return s.x + r > world.solidX0[i]! && s.x - r < world.solidX1[i]!;
}

/** Distance the body still has to travel to be clear of solid `i` on its far side (0 once clear). */
function toFarEdge(world: World, s: SimState, t: CompiledTuning, i: number, dir: number): number {
  const r = t.capsuleRadius;
  const d = dir > 0 ? world.solidX1[i]! - (s.x - r) : s.x + r - world.solidX0[i]!;
  return d > 0 ? d : 0;
}

/** The solid the body passes through this tick: the vault's box while the hands take the weight, else -1. */
export function plantSkip(s: SimState): number {
  if (s.plantHands !== 1 || s.plantBox < 0) return -1;
  return s.planted === PlantPhase.Takeoff ||
    s.planted === PlantPhase.Support ||
    s.planted === PlantPhase.Mounting ||
    s.planted === PlantPhase.Mantling
    ? s.plantBox
    : -1;
}

/** How far the feet rise with the tuck alone after `time` s in the air, starting from tuck `tuck0`. */
function tuckLift(tuck0: number, t: CompiledTuning, time: number): number {
  if (t.vaultTuck <= tuck0) return 0;
  const reached = tuck0 + time * t.tuckInRate;
  const tuck = reached < t.vaultTuck ? reached : t.vaultTuck;
  return comHeight(t, tuck0) - comHeight(t, tuck);
}

/**
 * The vault's takeoff time before the face, s: a flight whose apex puts the mass (the hips)
 * vaultHipClearance over a top `height` above the feet, starting from tuck `tuck0`.
 */
export function vaultTakeoffTime(tuck0: number, t: CompiledTuning, height: number): number {
  return sqrt((2 * vaultRise(tuck0, t, height)) / t.gravity);
}

/** How far the vault's flight lifts the mass: onto the hip clearance, and never less than that clearance (a low box still gets a hop). */
function vaultRise(tuck0: number, t: CompiledTuning, height: number): number {
  const rise = height + t.vaultHipClearance - comHeight(t, tuck0);
  return rise > t.vaultHipClearance ? rise : t.vaultHipClearance;
}

/**
 * The hop's takeoff time, s, before the body reaches the middle of the box (`beyond` s past its face):
 * the apex comes over the middle, and at the face the tucked feet are already vaultClearance over the top.
 */
function hopTakeoffTime(tuck0: number, t: CompiledTuning, height: number, beyond: number): number {
  const need = height + t.vaultClearance;
  let lo = beyond;
  let hi = beyond + 1;
  for (let i = 0; i < 24; i++) {
    const mid = 0.5 * (lo + hi);
    const atFace = mid - beyond;
    const feet = t.gravity * mid * atFace - 0.5 * t.gravity * atFace * atFace + tuckLift(tuck0, t, atFace);
    if (feet >= need) hi = mid;
    else lo = mid;
  }
  return hi;
}

function takeoff(
  world: World,
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  box: number,
  time: number,
  hands: boolean,
  grade: number,
  graded: boolean,
): void {
  const dir = s.vx !== 0 ? sign(s.vx) : s.facing;
  const entry = abs(s.vx);
  const height = world.solidY1[box]! - s.y;
  const tf = time > DT ? time : DT;
  let vy: number;
  if (hands) {
    // On time the apex is at the face; late, the flight is still rising there.
    const onTime = vaultTakeoffTime(s.tuck, t, height);
    vy = time >= onTime ? t.gravity * onTime : vaultRise(s.tuck, t, height) / tf + 0.5 * t.gravity * tf;
  } else {
    // `time` is to the middle of the box: the apex comes there.
    vy = t.gravity * tf;
  }
  if (vy > t.vaultPushMax) vy = t.vaultPushMax;
  if (vy < 0) vy = 0;
  const retain = !hands
    ? 1
    : grade === Grade.Perfect
      ? 1
      : grade === Grade.Good
        ? t.vaultRetainGood
        : t.vaultRetainOk;
  const boost = hands && grade === Grade.Perfect ? t.vaultPerfectBoost : 0;
  s.plantTargetVx = dir * (entry * retain + boost);
  s.ay = (vy - s.vy) / DT;
  s.vy = vy;
  s.grounded = 0;
  s.coyote = 0;
  s.slideCoyote = 0;
  s.jumpCutArmed = 0;
  s.tumbling = 0;
  s.plantBox = box;
  s.planted = hands ? PlantPhase.Takeoff : PlantPhase.Released;
  s.plantHands = hands ? 1 : 0;
  s.vaultArmBox = -1;
  s.plantTicks = 0;
  s.facing = dir;
  if (hands) {
    s.lastVaultGrade = grade;
    ev.push(Ev.Planted, s.tick, box, vy);
    if (!graded) emitGrade(s, t, ev, GradeKind.Vault, grade, entry);
    setState(s, ev, S.Plant);
  } else {
    setState(s, ev, S.Fall);
  }
}

/**
 * On a grounded tick before the tuck updates: a Tuck press inside a vaultable box's window arms the
 * vault and is graded on its tick. An armed press is the hands getting ready, so it neither crouches
 * nor slides (updateTuck and the slide read vaultArmBox).
 */
export function armVault(
  world: World,
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  tuckPressed: boolean,
): void {
  const speed = abs(s.vx);
  if (speed < 1e-6) {
    s.vaultArmBox = -1;
    return;
  }
  if (!tuckPressed) return;
  const box = world.nextObstacleAhead(
    s.x,
    s.y,
    t.capsuleRadius,
    t.standHeight,
    sign(s.vx),
    SolidClass.Box,
    t.vaultLookahead,
  );
  if (box < 0) return;
  const height = world.solidY1[box]! - s.y;
  if (height <= t.hurdleStepHeight || height > t.hurdleMaxHeight) return;
  const leadMs = (world.queryDist / speed - vaultTakeoffTime(s.tuck, t, height)) * 1000;
  if (leadMs > t.vaultWindowMs) return;
  s.vaultArmBox = box;
  s.vaultArmGrade =
    leadMs < 0
      ? Grade.Ok
      : gradeOffset(
          t.vaultPerfectLeadMs - leadMs,
          t.vaultPerfectHalfMs,
          t.vaultWindowMs - t.vaultPerfectLeadMs,
          t.vaultPerfectLeadMs,
        );
  emitGrade(s, t, ev, GradeKind.Vault, s.vaultArmGrade, leadMs);
}

/**
 * A grounded tick before the move: the hop over a knee-high box, the vault over a plantable one (a
 * Tuck press in the window is graded on its tick and takes off at the takeoff point; without a press
 * the body runs into the box), and the slide's grade against a pipe.
 */
export function groundHurdles(
  world: World,
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  ev: EventBuffer,
  tuckPressed: boolean,
): void {
  const speed = abs(s.vx);
  if (speed < 1e-6) {
    s.vaultArmBox = -1;
    return;
  }
  const dir = sign(s.vx);
  const r = t.capsuleRadius;
  const box = world.nextObstacleAhead(s.x, s.y, r, t.standHeight, dir, SolidClass.Box, t.vaultLookahead);
  if (box >= 0) {
    const height = world.solidY1[box]! - s.y;
    const toFace = world.queryDist / speed;
    if (height <= t.hurdleMaxHeight) {
      if (height <= t.hurdleStepHeight) {
        const beyond = ((world.solidX1[box]! - world.solidX0[box]!) * 0.5 + r) / speed;
        const at = hopTakeoffTime(s.tuck, t, height, beyond);
        if (toFace + beyond <= at + DT * 0.5 && !input.tuck)
          takeoff(world, s, t, ev, box, toFace + beyond, false, Grade.None, true);
        return;
      }
      const at = vaultTakeoffTime(s.tuck, t, height);
      // Take off on the tick the takeoff point falls inside, so the apex is at most a tick late.
      const due = toFace <= at + DT * 0.5;
      // Only a press vaults (owner, 2026-09-14: no automatic vault). An armed press takes off at the
      // takeoff point; a press after it takes off at once, late and still rising at the face.
      if (s.vaultArmBox === box && due) {
        takeoff(world, s, t, ev, box, toFace, true, s.vaultArmGrade, true);
        return;
      }
      if (s.vaultArmBox === box) return;
    }
  }
  if (tuckPressed && speed >= t.slideSpeed) {
    const pipe = world.nextObstacleAhead(s.x, s.y, r, t.standHeight, dir, SolidClass.Pipe, t.vaultLookahead);
    if (pipe >= 0) {
      const toFaceMs = (world.queryDist / speed) * 1000;
      const grade =
        toFaceMs > t.slideWindowMs || toFaceMs < t.slideWindowLateMs
          ? Grade.Ok
          : gradeOffset(
              t.slidePerfectMs - toFaceMs,
              t.slidePerfectHalfMs,
              t.slideWindowMs - t.slidePerfectMs,
              t.slidePerfectMs - t.slideWindowLateMs,
            );
      s.lastSlideGrade = grade;
      if (grade === Grade.Perfect && t.slidePerfectBoost > 0 && s.slideBoostPipe !== pipe) {
        s.slideBoostPipe = pipe;
        s.slideBoostDir = dir;
        s.slideBoostReady = 1;
      }
      emitGrade(s, t, ev, GradeKind.Slide, grade, toFaceMs);
    }
  }
  if (s.slideBoostReady === 1) {
    const pipe = s.slideBoostPipe;
    if ((!input.tuck && s.tuckFloor === 0) || dir !== s.slideBoostDir || speed < t.crouchSpeed) {
      s.slideBoostReady = 0;
      s.slideBoostPipe = -1;
    } else if (s.y + capsuleHeight(t, s.tuck) <= world.solidY0[pipe]!) {
      // Accelerating before the crouch fits would turn a perfectly timed slide into a head hit.
      s.vx += dir * t.slidePerfectBoost;
      s.slideBoostReady = 0;
      ev.push(Ev.SlideBoost, s.tick, pipe, t.slidePerfectBoost, s.vx);
    }
  }
}

/**
 * An airborne tick of a vault or a hop: no steering. On the way to the box the flight is ballistic
 * and keeps its speed. Over it the hands hold the body up (a rising body still slows under gravity,
 * then holds) and the speed eases to the grade's retention; mounting, the hands press the body up.
 * Mantling, the arms pull the body straight up the face at mantlePullSpeed with the hands on the
 * ledge until the shoulders clear the top; then the body goes up and over the edge, the speed
 * easing to what the grade kept, and the pull eases off so the feet come over by mantleClearance
 * and no more (the hands press the body onto the ledge, not into a hop off it).
 */
export function tickPlanted(world: World, s: SimState, t: CompiledTuning): void {
  const v0 = s.vx;
  const mantling = s.planted === PlantPhase.Mantling;
  const top = mantling ? world.solidY1[s.plantBox]! : 0;
  const pulling = mantling && s.y + shoulderHeight(t, s.tuck) < top;
  if (pulling) s.vx = 0;
  else if (s.planted === PlantPhase.Support || s.planted === PlantPhase.Mounting || mantling) {
    const ticks = t.vaultRetainTicks > 0 ? t.vaultRetainTicks : 1;
    let left = ticks - s.plantTicks;
    if (t.hurdleFlow && !mantling && s.lastVaultGrade === Grade.Perfect && s.plantTargetVx * v0 > 0) {
      // A narrow box can end support before vaultRetainMs elapses. Deliver the earned push
      // smoothly within the remaining contact, rather than discarding part of the reward.
      const remaining = toFarEdge(world, s, t, s.plantBox, sign(v0));
      const contactTicks = Math.max(1, Math.ceil(remaining / (abs(s.plantTargetVx) * DT)));
      if (contactTicks < left) left = contactTicks;
    }
    s.vx = left > 0 ? v0 + (s.plantTargetVx - v0) / left : s.plantTargetVx;
  }
  if (!pulling) s.plantTicks++;
  s.ax = (s.vx - v0) / DT;
  const vy0 = s.vy;
  if (mantling) {
    let vy = moveToward(vy0, t.mantlePullSpeed, 2 * t.gravity * DT);
    // Arriving: the speed that gravity alone would bring to rest mantleClearance over the top, so the
    // pull eases off at exactly the fall gravity and the feet stop just over the edge.
    const left = top + t.mantleClearance - s.y;
    const cap = left > 0 ? sqrt(2 * t.gravity * left) : 0;
    if (vy > cap) vy = cap;
    s.vy = vy;
  } else if (s.planted === PlantPhase.Mounting) {
    s.vy = moveToward(vy0, t.vaultMountPush, 2 * t.gravity * DT);
  } else {
    const g = vy0 <= 0 ? t.gravity * t.gravityFallScale : t.gravity;
    s.vy = vy0 - g * DT;
    if (s.planted === PlantPhase.Support && s.vy < 0) s.vy = 0;
    if (s.vy < -t.terminalVelocity) s.vy = -t.terminalVelocity;
  }
  s.ay = (s.vy - vy0) / DT;
}

/**
 * After the move: advance the plant's phase, report a mount when the body lands on the box's top,
 * and a hit when a box or pipe face stopped the body at hitSpeed or more.
 */
export function afterMoveHurdles(
  world: World,
  s: SimState,
  t: CompiledTuning,
  ev: EventBuffer,
  wallSolid: number,
  wallSpeed: number,
): void {
  if (s.slideBoostReady === 1 && s.grounded === 0) {
    s.slideBoostReady = 0;
    s.slideBoostPipe = -1;
  }
  const boostedPipe = s.slideBoostPipe;
  if (boostedPipe >= 0) {
    // One reward per passage, even if Tuck is tapped repeatedly inside its timing window.
    // Clearing the pipe (or backing out of its approach) allows a fresh attempt from either side.
    const left = world.solidX0[boostedPipe]!;
    const right = world.solidX1[boostedPipe]!;
    const near = s.slideBoostDir > 0 ? s.x - t.capsuleRadius > right : s.x + t.capsuleRadius < left;
    const retreated =
      s.slideBoostDir > 0
        ? s.x + t.capsuleRadius < left - t.vaultLookahead
        : s.x - t.capsuleRadius > right + t.vaultLookahead;
    if (near || retreated) {
      s.slideBoostPipe = -1;
      s.slideBoostReady = 0;
    }
  }
  const box = s.plantBox;
  if (box >= 0 && s.planted !== PlantPhase.None) {
    const dir = sign(s.plantTargetVx) || s.facing;
    const top = world.solidY1[box]!;
    const over = overlapsX(world, s, t, box);
    const remaining = toFarEdge(world, s, t, box, dir);
    if (s.barId >= 0) {
      endPlant(s);
    } else if (s.grounded === 1) {
      // Landed: on the box's top it is a mount (the ground probe settles the feet within a few cm); a
      // mantle ends the same way, on the ledge, and says so.
      if (over && abs(s.y - top) < 0.05 && s.plantHands === 1)
        ev.push(Ev.Mounted, s.tick, box, s.mantling, s.mantling === 1 ? s.lastMantleGrade : 0);
      endPlant(s);
    } else if (wallSolid === box || remaining <= 0) {
      endPlant(s);
      if (s.state === S.Plant) setState(s, ev, S.Fall);
    } else if (s.planted === PlantPhase.Takeoff) {
      if (over) {
        s.planted = PlantPhase.Support;
        s.plantTicks = 0;
      }
    } else if (s.planted === PlantPhase.Support) {
      // Can the hands carry the body past the far edge in the support time left? If not, mount.
      const ticksLeft = t.vaultSupportTicks - s.plantTicks;
      const speed = abs(s.vx);
      if (speed * DT * ticksLeft < remaining) s.planted = PlantPhase.Mounting;
    } else if (s.planted === PlantPhase.Mounting || s.planted === PlantPhase.Mantling) {
      // Once the feet are over the top the box (or the ledge) is solid again and the body comes down on it.
      if (s.y >= top) s.planted = PlantPhase.Released;
    }
  }
  if (wallSolid >= 0 && wallSpeed >= t.hitSpeed) {
    const cls = world.solidClass[wallSolid];
    if (cls === SolidClass.Box || cls === SolidClass.Pipe) {
      ev.push(Ev.Hit, s.tick, wallSolid, wallSpeed);
      emitGrade(s, t, ev, cls === SolidClass.Box ? GradeKind.Vault : GradeKind.Slide, Grade.Miss, wallSpeed);
    }
  }
}

function endPlant(s: SimState): void {
  s.plantBox = -1;
  s.planted = PlantPhase.None;
  s.plantHands = 0;
  s.mantling = 0;
}
